28
import React, { memo, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAppThunkDispatch } from "redux/store";
import { RootState } from "redux/store";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import createCache from "@emotion/cache";
import { TssCacheProvider } from "tss-react";
import { getTheme } from "./theme";
import * as Router from "react-router-dom";
import { routes } from "routes/routes";
import { NotFoundPage } from "./component/page/not-found";
import {
	getUserUnsafe,
	getLeadsOptimized,
	getMarketsOptimized,
} from "redux/selector";
import {
	monitorCredentials,
} from "redux/slice/authentication";
import { urls } from "routes/urls";
import { fetchAdminMode } from "redux/slice/admin-mode";
import { createPing } from "redux/slice/ping";
import { fetchQuota } from "redux/slice/quota";
import { fetchLeads } from "redux/slice/leads";
import { fetchLeadLastActivities } from "redux/slice/lead-last-activities";
import { fetchLeadLastSearchAgentChats } from "redux/slice/lead-last-search-agent-chats";
import { hasSearchAgent } from "shared/search-agent-permission";
import { fetchLeadTags } from "redux/slice/lead-tags";
import { fetchLeadPhones } from "redux/slice/lead-phones";
import { fetchLeadAnniversaries } from "redux/slice/lead-anniversaries";
import { fetchLeadPipelines } from "redux/slice/lead-pipelines";
import { fetchLeadNotes } from "redux/slice/lead-notes";
import { fetchCampaigns } from "redux/slice/campaigns";
import { fetchCampaignDisplayCategories } from "redux/slice/campaign-display-categories";
import { fetchCampaignSubscriptionStats } from "redux/slice/campaign-subscription-stats";
import { fetchCampaignMessages } from "redux/slice/campaign-messages";
import { fetchCampaignCampaignMessages } from "redux/slice/campaign-campaign-messages";
import { fetchCampaignSubscriptions } from "redux/slice/campaign-subscriptions";
import { fetchCampaignEmails } from "redux/slice/campaign-emails";
import { fetchCampaignStats } from "redux/slice/campaign-stats";
import { fetchCampaignCampaignMessageStats } from "redux/slice/campaign-campaign-message-stats";
import { fetchCampaignsSettings } from "redux/slice/campaigns-settings";
import { fetchCampaignRoutes } from "redux/slice/campaign-routes";
import { fetchMarkets } from "redux/slice/markets";
import { fetchMarketReports } from "redux/slice/market-reports";
import { fetchSavedSearches } from "redux/slice/saved-searches";
import { fetchPostalCodes } from "redux/slice/postal-code";
import { fetchAgents } from "redux/slice/agents";
import { fetchRoundRobinRoutes } from "redux/slice/round-robin-routes";
import { fetchGeographicRoutes } from "redux/slice/geographic-routes";
import { fetchOffices } from "redux/slice/offices";
import { fetchCities } from "redux/slice/cities";
import { fetchNeighborhoods } from "redux/slice/neighborhoods";
import { fetchTasks } from "redux/slice/tasks";
import { fetchOneUser } from "redux/slice/one-users";
import { fetchSiteLinks } from "redux/slice/site-links";
import { fetchIntegrations } from "redux/slice/integrations";
import { fetchAgentBoards } from "redux/slice/agent-boards";
import { fetchOfficeBoards } from "redux/slice/office-boards";
import { fetchBoards } from "redux/slice/boards";
import { fetchLanguages } from "redux/slice/languages";
import { fetchMarketListings } from "redux/slice/market-listings";
import { fetchMarketSettings } from "redux/slice/market-settings";
import sessionTimer from "shared/session-timer";
import { SessionExpiredDialog } from "component/shared/session-expired-dialog";
import * as appcues from "shared/appcues";
import * as profitwell from "shared/profit-well";
import ReactGA from "react-ga4";
import * as env from "shared/env";
import { LoadingPage } from "component/page/loading";
import { LoginPage } from "component/page/login";
import { UserVoiceTrigger } from "component/shared/uservoice-trigger";
import { ErrorPageContext } from "component/layout/error/context";

// Landmine 5 (insertion-order / specificity shift, MAX-3909 scope 3f).
// Pre-3f, app styles were authored with JSS and its bridge injected the app's
// <style> sheets AFTER emotion, so an app className (e.g. page-header `.title`
// fontWeight 700) won the equal-specificity tie against MUI's theme/variant
// styles (e.g. h1 fontWeight 500). After converting to tss-react, app styles
// live in emotion too. By default tss-react's mui variant reuses MUI's own
// emotion cache (created with prepend:true by <StyledEngineProvider injectFirst>),
// so MUI's variant styles and the app's overrides share one cache and MUI's
// theme styles silently win equal-specificity ties — headings render narrower/
// lighter, and the same collision affects every app className override that
// touches a MUI-set property.
//
// tss-react's documented fix ("my tss styles lose to MUI styles"): give tss-react
// a SEPARATE emotion cache, provided via TssCacheProvider. MUI's cache is prepended
// (top of <head>) by injectFirst; this tss cache uses the default (append), so tss
// sheets land later in <head> and win equal-specificity ties app-wide. Created once
// at module level so the cache is stable across renders.
const tssCache = createCache({ key: "tss" });

export const BaseComponent: React.FC<Router.RouteComponentProps> = (props) => {
	const user = useSelector(getUserUnsafe);
	const directClient = user?.reseller.direct;
	const leadsLoading = useSelector((state: RootState) => state.leads.loading);
	const marketsLoading = useSelector((state: RootState) => state.markets.loading);
	const leads = useSelector(getLeadsOptimized);
	const markets = useSelector(getMarketsOptimized);
	const dispatch = useAppThunkDispatch();
	const { history, location } = props;

	const [sessionHasExpired, setSessionHasExpired] = useState(false);
	const [isErrorPage, setIsErrorPage] = useState(false);
	const pingInterval = useRef<NodeJS.Timeout>();
	const quotaInterval = useRef<NodeJS.Timeout>();
	const prevUser = useRef<typeof user>();
	const appcuesInitialized = useRef<boolean>(false);


	// const renderCountRef = useRef(0);
	// const lastRenderTime = useRef(0);

	// // Count unique renders (accounting for StrictMode double rendering)
	// const now = Date.now();
	// if (now - lastRenderTime.current > 10) { // Only count if more than 10ms apart
	// 	renderCountRef.current += 1;
	// 	console.log(`BaseComponent unique render #${renderCountRef.current}`);
	// }
	// lastRenderTime.current = now;

	useEffect(() => {
		const timer = sessionTimer();
		const limit = 15;
		timer.start();
		timer.activityListener(() => {
			const minutes = Math.floor(timer.get() / 60);
			if (minutes >= limit) {
				setSessionHasExpired(true);
			}
			timer.reset();
		});
	}, []);

	useEffect(() => {
		dispatch(monitorCredentials());
		
		const tryInitializeAppcues = () => {
			if (appcuesInitialized.current || !user) return;
			
			if (user.reseller.direct === true) {
				const leadsTotal = user.permissions.leads ? leads.length : 0;
				const marketsTotal = user.permissions.markets ? markets.length : 0;
				appcues.identify(user, leadsTotal, marketsTotal);
				appcuesInitialized.current = true;
			}
		};
		
		if (user && !appcuesInitialized.current) {
			tryInitializeAppcues();
		}
		
		// eslint-disable-next-line
	}, [user?.agentId, user?.reseller?.direct]);

	useEffect(() => {
		if (user && appcuesInitialized.current && user.reseller.direct === true) {
			const leadsTotal = user.permissions.leads ? leads.length : 0;
			const marketsTotal = user.permissions.markets ? markets.length : 0;
			appcues.identify(user, leadsTotal, marketsTotal);
		}
		// eslint-disable-next-line
	}, [leads.length, markets.length]);

	// Initialize GA4 and track page views
	useEffect(() => {
		if (env.googleAnalytics && env.googleAnalyticsEnabled) {
			ReactGA.initialize(env.googleAnalytics);
			ReactGA.send({ hitType: "pageview", page: location.pathname });
		}
	}, [user, location.pathname]);

	useEffect(() => {
		if (!prevUser.current && user) {
			profitwell.start(user);
			refreshData();
		}
		if (prevUser.current && !user) {
			history.push(urls.logout(prevUser.current.reseller));
			appcues.reset();
		}
		prevUser.current = user;
		// eslint-disable-next-line
	}, [user?.agentId, user?.clientId]);

	const refreshData = () => {
		if (user) {
			dispatch(fetchAdminMode());
			if (user.permissions.oneCrm) dispatch(fetchOneUser());
			if (user.permissions.leads) dispatch(fetchLeads());
			if (!pingInterval.current) {
				dispatch(createPing({ ping: {} }));
				pingInterval.current = setInterval(() => {
					if (user) dispatch(createPing({ ping: {} }));
				}, 60 * 1000);
			}
			if (!quotaInterval.current) {
				dispatch(fetchQuota());
				quotaInterval.current = setInterval(() => {
					if (user) dispatch(fetchQuota());
				}, 60 * 1000);
			}
			if (user.permissions.leadLastActive) dispatch(fetchLeadLastActivities());
			if (hasSearchAgent(user)) dispatch(fetchLeadLastSearchAgentChats());
			if (user.permissions.crmTags) dispatch(fetchLeadTags());
			dispatch(fetchLeadPhones());
			if (user.permissions.crmAnniversary) dispatch(fetchLeadAnniversaries());
			if (user.permissions.crmPipeline) dispatch(fetchLeadPipelines());
			if (user.permissions.crmNotes) dispatch(fetchLeadNotes());
			if (user.permissions.crmTasks) dispatch(fetchTasks());
			if (user.permissions.roundRobinRouting) dispatch(fetchRoundRobinRoutes());
			if (user.permissions.geographicRouting) {
				dispatch(fetchGeographicRoutes());
				dispatch(fetchPostalCodes());
			}
			if (user.permissions.campaigns) {
				dispatch(fetchCampaigns());
				dispatch(fetchCampaignDisplayCategories());
				dispatch(fetchCampaignStats());
				dispatch(fetchCampaignCampaignMessageStats());
				dispatch(fetchCampaignMessages());
				dispatch(fetchCampaignCampaignMessages());
				dispatch(fetchCampaignSubscriptions());
				dispatch(fetchCampaignEmails({ blast: true }));
				dispatch(fetchCampaignsSettings());
				dispatch(fetchCampaignRoutes());
				dispatch(fetchCampaignSubscriptionStats());
				dispatch(fetchCampaignEmails({ blast: true }));
			}
			if (user.permissions.leadSaveSearch) dispatch(fetchSavedSearches());
			dispatch(fetchBoards());
			dispatch(fetchCities());
			dispatch(fetchNeighborhoods());
			dispatch(fetchSiteLinks());
			dispatch(fetchIntegrations());
			dispatch(fetchLanguages());
			if (user.permissions.markets) {
				dispatch(fetchMarketListings());
				dispatch(fetchMarketReports());
				dispatch(fetchMarkets());
				dispatch(fetchMarketSettings());
			}
			dispatch(fetchAgents());
			dispatch(fetchAgentBoards());
			if (user.permissions.broker) {
				dispatch(fetchOffices());
				dispatch(fetchOfficeBoards());
			}
		}
	};

	useEffect(() => {
		return () => {
			if (pingInterval.current) clearInterval(pingInterval.current);
			if (quotaInterval.current) clearInterval(quotaInterval.current);
		};
	}, []);

	const theme = user
		? getTheme(user.reseller.primaryColor, user.reseller.secondaryColor, user.reseller.navBackgroundColor)
		: getTheme();
	// The v5 ThemeProvider serves @mui/material components and tss-react's makeStyles/withStyles
	// (both on emotion). StyledEngineProvider injectFirst prepends MUI's own cache to the top of
	// <head>; TssCacheProvider (below) routes all tss-react styling onto a separate, appended cache
	// so app overrides win equal-specificity ties (see tssCache / Landmine 5 note above). Date/time
	// pickers render via the v6 @mui/x-date-pickers LocalizationProvider (AdapterDateFns).

	return (
		<StyledEngineProvider injectFirst>
		<TssCacheProvider value={tssCache}>
		<ErrorPageContext.Provider value={setIsErrorPage}>
		<ThemeProvider theme={theme}>
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<Router.Switch>
				{routes.map((route, index) => (
					<Router.Route
						key={index}
						path={route.path}
						exact
						render={(routeProps) => {
							if (route.authenticated) {
								if (user === undefined) {
									return <LoadingPage />;
								}
								if (user === null) {
									return <LoginPage />;
								}
							}
							return <route.component {...routeProps} user={user} />;
						}}
					/>
				))}
				<Router.Route component={NotFoundPage} />
			</Router.Switch>
			<SessionExpiredDialog
				open={sessionHasExpired}
				onClick={() => window.location.reload()}
			/>
			{user && directClient && !user.demo && !isErrorPage && <UserVoiceTrigger user={user} />}
		</LocalizationProvider>
		</ThemeProvider>
		</ErrorPageContext.Provider>
		</TssCacheProvider>
		</StyledEngineProvider>
	);
};

const MemoizedBase = memo(BaseComponent);

export default Router.withRouter(MemoizedBase);
